import type {
  ApiErrorState,
  ApiPaginatedResponse,
  BrandProductsPayload,
  MarketplaceHomePayload,
  Product,
  ProductPatchPayload,
  ProductPayload
} from "../../types/marketplace";
import * as constants from "../constants/marketplace.constants";

export interface MarketplaceHomeRequestedAction {
  type: typeof constants.MARKETPLACE_HOME_REQUESTED;
}

export interface MarketplaceHomeSucceededAction {
  type: typeof constants.MARKETPLACE_HOME_SUCCEEDED;
  data: MarketplaceHomePayload;
}

export interface MarketplaceHomeFailedAction {
  type: typeof constants.MARKETPLACE_HOME_FAILED;
  error: ApiErrorState;
}

export interface MarketplaceSearchRequestedAction {
  type: typeof constants.MARKETPLACE_SEARCH_REQUESTED;
  value: string;
}

export interface MarketplaceSearchSucceededAction {
  type: typeof constants.MARKETPLACE_SEARCH_SUCCEEDED;
  data: Product[];
}

export interface MarketplaceSearchFailedAction {
  type: typeof constants.MARKETPLACE_SEARCH_FAILED;
  error: ApiErrorState;
}

export interface MarketplaceSearchClearedAction {
  type: typeof constants.MARKETPLACE_SEARCH_CLEARED;
}

export interface MarketplaceProductsPageRequestedAction {
  type: typeof constants.MARKETPLACE_PRODUCTS_PAGE_REQUESTED;
  page: number;
  perPage: number;
}

export interface MarketplaceProductsPageSucceededAction {
  type: typeof constants.MARKETPLACE_PRODUCTS_PAGE_SUCCEEDED;
  data: ApiPaginatedResponse<Product>;
}

export interface MarketplaceProductsPageFailedAction {
  type: typeof constants.MARKETPLACE_PRODUCTS_PAGE_FAILED;
  error: ApiErrorState;
}

export interface MarketplaceBrandProductsRequestedAction {
  type: typeof constants.MARKETPLACE_BRAND_PRODUCTS_REQUESTED;
  clientId: number;
  page: number;
  perPage: number;
  category?: string;
}

export interface MarketplaceBrandProductsSucceededAction {
  type: typeof constants.MARKETPLACE_BRAND_PRODUCTS_SUCCEEDED;
  data: BrandProductsPayload;
}

export interface MarketplaceBrandProductsFailedAction {
  type: typeof constants.MARKETPLACE_BRAND_PRODUCTS_FAILED;
  error: ApiErrorState;
}

export interface ProductCreateRequestedAction {
  type: typeof constants.PRODUCT_CREATE_REQUESTED;
  payload: ProductPayload;
}

export interface ProductCreateSucceededAction {
  type: typeof constants.PRODUCT_CREATE_SUCCEEDED;
  data: Product;
}

export interface ProductCreateFailedAction {
  type: typeof constants.PRODUCT_CREATE_FAILED;
  error: ApiErrorState;
}

export interface ProductUpdateRequestedAction {
  type: typeof constants.PRODUCT_UPDATE_REQUESTED;
  id: string;
  payload: ProductPayload;
}

export interface ProductUpdateSucceededAction {
  type: typeof constants.PRODUCT_UPDATE_SUCCEEDED;
  data: Product;
}

export interface ProductUpdateFailedAction {
  type: typeof constants.PRODUCT_UPDATE_FAILED;
  error: ApiErrorState;
}

export interface ProductPatchRequestedAction {
  type: typeof constants.PRODUCT_PATCH_REQUESTED;
  id: string;
  payload: ProductPatchPayload;
}

export interface ProductPatchSucceededAction {
  type: typeof constants.PRODUCT_PATCH_SUCCEEDED;
  data: Product;
}

export interface ProductPatchFailedAction {
  type: typeof constants.PRODUCT_PATCH_FAILED;
  error: ApiErrorState;
}

export interface ProductDeleteRequestedAction {
  type: typeof constants.PRODUCT_DELETE_REQUESTED;
  id: string;
}

export interface ProductDeleteSucceededAction {
  type: typeof constants.PRODUCT_DELETE_SUCCEEDED;
  id: string;
}

export interface ProductDeleteFailedAction {
  type: typeof constants.PRODUCT_DELETE_FAILED;
  error: ApiErrorState;
}

export type MarketplaceAction =
  | MarketplaceHomeRequestedAction
  | MarketplaceHomeSucceededAction
  | MarketplaceHomeFailedAction
  | MarketplaceSearchRequestedAction
  | MarketplaceSearchSucceededAction
  | MarketplaceSearchFailedAction
  | MarketplaceSearchClearedAction
  | MarketplaceProductsPageRequestedAction
  | MarketplaceProductsPageSucceededAction
  | MarketplaceProductsPageFailedAction
  | MarketplaceBrandProductsRequestedAction
  | MarketplaceBrandProductsSucceededAction
  | MarketplaceBrandProductsFailedAction
  | ProductCreateRequestedAction
  | ProductCreateSucceededAction
  | ProductCreateFailedAction
  | ProductUpdateRequestedAction
  | ProductUpdateSucceededAction
  | ProductUpdateFailedAction
  | ProductPatchRequestedAction
  | ProductPatchSucceededAction
  | ProductPatchFailedAction
  | ProductDeleteRequestedAction
  | ProductDeleteSucceededAction
  | ProductDeleteFailedAction;

export const marketplaceHomeRequested = (): MarketplaceHomeRequestedAction => ({
  type: constants.MARKETPLACE_HOME_REQUESTED
});

export const marketplaceHomeSucceeded = (
  data: MarketplaceHomePayload
): MarketplaceHomeSucceededAction => ({
  data,
  type: constants.MARKETPLACE_HOME_SUCCEEDED
});

export const marketplaceHomeFailed = (
  error: ApiErrorState
): MarketplaceHomeFailedAction => ({
  error,
  type: constants.MARKETPLACE_HOME_FAILED
});

export const marketplaceSearchRequested = (
  value: string
): MarketplaceSearchRequestedAction => ({
  type: constants.MARKETPLACE_SEARCH_REQUESTED,
  value
});

export const marketplaceSearchSucceeded = (
  data: Product[]
): MarketplaceSearchSucceededAction => ({
  data,
  type: constants.MARKETPLACE_SEARCH_SUCCEEDED
});

export const marketplaceSearchFailed = (
  error: ApiErrorState
): MarketplaceSearchFailedAction => ({
  error,
  type: constants.MARKETPLACE_SEARCH_FAILED
});

export const marketplaceSearchCleared = (): MarketplaceSearchClearedAction => ({
  type: constants.MARKETPLACE_SEARCH_CLEARED
});

export const marketplaceProductsPageRequested = (
  page: number,
  perPage = 50
): MarketplaceProductsPageRequestedAction => ({
  page,
  perPage,
  type: constants.MARKETPLACE_PRODUCTS_PAGE_REQUESTED
});

export const marketplaceProductsPageSucceeded = (
  data: ApiPaginatedResponse<Product>
): MarketplaceProductsPageSucceededAction => ({
  data,
  type: constants.MARKETPLACE_PRODUCTS_PAGE_SUCCEEDED
});

export const marketplaceProductsPageFailed = (
  error: ApiErrorState
): MarketplaceProductsPageFailedAction => ({
  error,
  type: constants.MARKETPLACE_PRODUCTS_PAGE_FAILED
});

export const marketplaceBrandProductsRequested = (
  clientId: number,
  page: number,
  perPage = 50,
  category?: string
): MarketplaceBrandProductsRequestedAction => ({
  category,
  clientId,
  page,
  perPage,
  type: constants.MARKETPLACE_BRAND_PRODUCTS_REQUESTED
});

export const marketplaceBrandProductsSucceeded = (
  data: BrandProductsPayload
): MarketplaceBrandProductsSucceededAction => ({
  data,
  type: constants.MARKETPLACE_BRAND_PRODUCTS_SUCCEEDED
});

export const marketplaceBrandProductsFailed = (
  error: ApiErrorState
): MarketplaceBrandProductsFailedAction => ({
  error,
  type: constants.MARKETPLACE_BRAND_PRODUCTS_FAILED
});

export const productCreateRequested = (
  payload: ProductPayload
): ProductCreateRequestedAction => ({
  payload,
  type: constants.PRODUCT_CREATE_REQUESTED
});

export const productCreateSucceeded = (
  data: Product
): ProductCreateSucceededAction => ({
  data,
  type: constants.PRODUCT_CREATE_SUCCEEDED
});

export const productCreateFailed = (
  error: ApiErrorState
): ProductCreateFailedAction => ({
  error,
  type: constants.PRODUCT_CREATE_FAILED
});

export const productUpdateRequested = (
  id: string,
  payload: ProductPayload
): ProductUpdateRequestedAction => ({
  id,
  payload,
  type: constants.PRODUCT_UPDATE_REQUESTED
});

export const productUpdateSucceeded = (
  data: Product
): ProductUpdateSucceededAction => ({
  data,
  type: constants.PRODUCT_UPDATE_SUCCEEDED
});

export const productUpdateFailed = (
  error: ApiErrorState
): ProductUpdateFailedAction => ({
  error,
  type: constants.PRODUCT_UPDATE_FAILED
});

export const productPatchRequested = (
  id: string,
  payload: ProductPatchPayload
): ProductPatchRequestedAction => ({
  id,
  payload,
  type: constants.PRODUCT_PATCH_REQUESTED
});

export const productPatchSucceeded = (
  data: Product
): ProductPatchSucceededAction => ({
  data,
  type: constants.PRODUCT_PATCH_SUCCEEDED
});

export const productPatchFailed = (
  error: ApiErrorState
): ProductPatchFailedAction => ({
  error,
  type: constants.PRODUCT_PATCH_FAILED
});

export const productDeleteRequested = (
  id: string
): ProductDeleteRequestedAction => ({
  id,
  type: constants.PRODUCT_DELETE_REQUESTED
});

export const productDeleteSucceeded = (
  id: string
): ProductDeleteSucceededAction => ({
  id,
  type: constants.PRODUCT_DELETE_SUCCEEDED
});

export const productDeleteFailed = (
  error: ApiErrorState
): ProductDeleteFailedAction => ({
  error,
  type: constants.PRODUCT_DELETE_FAILED
});
